import { useEffect, useMemo, useState } from 'react';
import {
  Clock,
  Plus,
  Edit2,
  Trash2,
  Check,
  AlertTriangle,
  Star,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button, Badge, Spinner, Modal, useToast } from '@centraoneplus/ui';
import { useSlaStore, CreateSlaPolicyDto } from '@stores/slaStore';
import { useBusinessHoursStore } from '@stores/businessHoursStore';
import type { BusinessHoursSchedule } from '@services/businessHours.service';
import type { SlaPolicy } from '@centraoneplus/shared-types';
import { isSamePayload } from '@utils/formDirty';
import SlaEscalationRulesEditor, { backfillRuleIds, canonicalizeActions } from './SlaEscalationRulesEditor';
import { escalationHistoryService, type EscalationConfigReport } from '@services/sla.service';
import {
  findings,
  ladderOf,
  reachOf,
  readingOrder,
  summarise,
  targetText,
  REACH_TEXT,
  type Ladder,
  type LadderState,
} from './slaPolicies';
import { ThemedSelect } from '@components/common/ThemedSelect';

const PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

interface SlaPolicyFormProps {
  policy?: SlaPolicy;
  schedules: BusinessHoursSchedule[];
  onSubmit: (data: CreateSlaPolicyDto) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function SlaPolicyForm({ policy, schedules, onSubmit, onCancel, isLoading }: SlaPolicyFormProps) {
  // Computed once on mount and reused as the dirty-check baseline, so the
  // comparison uses the same defaulting the form was seeded with.
  const [initialData] = useState<CreateSlaPolicyDto>(() => ({
    name: policy?.name || '',
    description: policy?.description || '',
    isDefault: policy?.isDefault || false,
    isActive: policy?.isActive ?? true,
    firstResponseTarget: policy?.firstResponseTarget || 60,
    nextResponseTarget: policy?.nextResponseTarget || 240,
    resolutionTarget: policy?.resolutionTarget || 1440,
    // Approaching-breach lead times. Left undefined when unset so the policy
    // keeps inheriting the built-in default rather than storing a value the
    // admin never chose; an empty field round-trips as "not configured".
    firstResponseWarning: policy?.firstResponseWarning,
    resolutionWarning: policy?.resolutionWarning,
    useBusinessHours: policy?.useBusinessHours ?? true,
    businessHoursId: policy?.businessHoursId ?? null,
    priorityTargets: policy?.priorityTargets || {
      urgent: { firstResponse: 15, resolution: 240 },
      high: { firstResponse: 30, resolution: 480 },
      medium: { firstResponse: 60, resolution: 1440 },
      low: { firstResponse: 120, resolution: 2880 },
    },
    // Seeded from the policy so editing any other field round-trips the ladder
    // instead of dropping it. Rules authored over the API that this form does
    // not render are carried through untouched.
    //
    // Backfill a stable id onto any level that lacks one, derived from its
    // CURRENT shape so identity is preserved (#15034). Without this, an
    // id-less level's identity is derived from its shape at sweep time, so
    // editing its threshold here re-armed it for every open ticket and
    // re-paged everyone. Seeding the id from the original shape means the id is
    // fixed BEFORE any edit, so tuning the threshold now keeps the same id.
    // The zero-lead `before_target` case inherits the policy's warning
    // threshold, so those are passed as context.
    escalationRules: backfillRuleIds(
      canonicalizeActions((policy?.escalationRules as CreateSlaPolicyDto['escalationRules']) || []),
      {
        firstResponseWarning: policy?.firstResponseWarning,
        resolutionWarning: policy?.resolutionWarning,
      },
    ) as CreateSlaPolicyDto['escalationRules'],
  }));
  const [formData, setFormData] = useState<CreateSlaPolicyDto>(initialData);

  const [showPriorityTargets, setShowPriorityTargets] = useState(false);
  const [showEscalation, setShowEscalation] = useState(
    (policy?.escalationRules?.length ?? 0) > 0,
  );

  // Edit mode: nothing to save until a value actually differs from the policy as
  // it stands in the list. Create mode has no baseline, so it's always "dirty".
  const isDirty = !policy || !isSamePayload(initialData, formData);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const updatePriorityTarget = (
    priority: string,
    field: 'firstResponse' | 'resolution',
    value: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      priorityTargets: {
        ...prev.priorityTargets,
        [priority]: {
          ...(prev.priorityTargets?.[priority] || { firstResponse: 60, resolution: 1440 }),
          [field]: value,
        },
      },
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
            Policy Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:border-gray-700"
            placeholder="e.g., Standard SLA"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:border-gray-700"
            placeholder="Optional description for this SLA policy"
            rows={2}
          />
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-700"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">Active</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-700"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">Set as default</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.useBusinessHours}
              onChange={(e) => setFormData({ ...formData, useBusinessHours: e.target.checked })}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-700"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">Use business hours</span>
          </label>
        </div>

        {formData.useBusinessHours && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
              Business Hours Schedule
            </label>
            <ThemedSelect
              value={formData.businessHoursId ?? ''}
              onChange={(e) =>
                setFormData({ ...formData, businessHoursId: e.target.value || null })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
            >
              <option value="">Default schedule</option>
              {schedules.map((s) => (
                <option key={s.id} value={s.id} disabled={!s.isActive}>
                  {s.name}
                  {s.isDefault ? ' (default)' : ''}
                  {!s.isActive ? ' — disabled' : ''}
                </option>
              ))}
            </ThemedSelect>
            {schedules.length === 0 ? (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                No business hours schedules found. Create one under Settings → Business Hours first.
              </p>
            ) : (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                SLA timers only count time inside these hours. Leave as &ldquo;Default schedule&rdquo;
                to use the tenant default.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Default Targets */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">Default Response Targets</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">First Response</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={formData.firstResponseTarget}
                onChange={(e) =>
                  setFormData({ ...formData, firstResponseTarget: Number(e.target.value) })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:border-gray-700"
                min={1}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Next Response</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={formData.nextResponseTarget}
                onChange={(e) =>
                  setFormData({ ...formData, nextResponseTarget: Number(e.target.value) })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:border-gray-700"
                min={1}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Resolution</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={formData.resolutionTarget}
                onChange={(e) =>
                  setFormData({ ...formData, resolutionTarget: Number(e.target.value) })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:border-gray-700"
                min={1}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
            </div>
          </div>
        </div>
      </div>

      {/* Approaching-breach warning thresholds */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-1 dark:text-gray-100">Approaching-breach Warning</h4>
        <p className="text-xs text-gray-500 mb-3 dark:text-gray-400">
          Lead time before a target is breached. A pre-breach escalation level with no lead of its
          own inherits this. Leave blank to use the built-in 15-minute default. Must be less than the
          matching target.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">First Response Warning</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={formData.firstResponseWarning ?? ''}
                placeholder="15"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    firstResponseWarning:
                      e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:border-gray-700"
                min={1}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Resolution Warning</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={formData.resolutionWarning ?? ''}
                placeholder="15"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    resolutionWarning:
                      e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:border-gray-700"
                min={1}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
            </div>
          </div>
        </div>
      </div>

      {/* Priority-based Targets */}
      <div>
        <button
          type="button"
          onClick={() => setShowPriorityTargets(!showPriorityTargets)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-200"
        >
          {showPriorityTargets ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          Priority-based Targets
        </button>

        {showPriorityTargets && (
          <div className="mt-3 space-y-3">
            {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg dark:bg-gray-900">
                <span className="w-20 text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">First Response</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={formData.priorityTargets?.[key]?.firstResponse || 60}
                        onChange={(e) =>
                          updatePriorityTarget(key, 'firstResponse', Number(e.target.value))
                        }
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                        min={1}
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">min</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Resolution</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={formData.priorityTargets?.[key]?.resolution || 1440}
                        onChange={(e) =>
                          updatePriorityTarget(key, 'resolution', Number(e.target.value))
                        }
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                        min={1}
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">min</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Escalation ladder */}
      <div>
        <button
          type="button"
          onClick={() => setShowEscalation(!showEscalation)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-200"
        >
          {showEscalation ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          Escalation Levels
          {(formData.escalationRules?.length ?? 0) > 0 && (
            <Badge variant="secondary">{formData.escalationRules?.length}</Badge>
          )}
        </button>

        {showEscalation && (
          <SlaEscalationRulesEditor
            value={formData.escalationRules ?? []}
            onChange={(escalationRules) => setFormData({ ...formData, escalationRules })}
          />
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={isLoading || !formData.name || !isDirty}
          title={!isDirty && policy ? 'No changes to save' : undefined}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              {policy ? 'Update Policy' : 'Create Policy'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

const LADDER_TONE: Record<LadderState, { color: string; background?: string; border?: string }> = {
  none: { color: 'var(--co-ink-4)' },
  live: { color: 'var(--co-green)' },
  untested: { color: 'var(--co-ink-3)' },
  inert: { color: 'var(--co-terracotta)' },
  broken: { color: 'var(--co-burgundy)' },
};

interface PolicyRowProps {
  policy: SlaPolicy;
  ladder: Ladder;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onSetDefault: () => void;
  isDeleting: boolean;
}

// One grid for the header and every row, sized by the card's own width because
// the nav rail and the settings column both take space from the viewport. Below
// 900px (the 1024 and 1280 screens) six columns cannot fit, so "Applies to"
// moves under the policy name and every other column keeps its place. The ranges
// do not overlap, so which template applies never depends on stylesheet order.
const ROW_GRID =
  'grid items-center gap-x-3 [grid-template-columns:minmax(0,1fr)_76px_76px_100px_124px] [@container(min-width:900px)_and_(max-width:1159px)]:[grid-template-columns:minmax(0,1fr)_156px_92px_92px_132px_124px] [@container(min-width:1160px)]:[grid-template-columns:minmax(0,1fr)_228px_112px_112px_164px_124px]';
const COL_REACH =
  'col-start-1 row-start-2 mt-[5px] pr-3 [@container(min-width:900px)]:col-start-auto [@container(min-width:900px)]:row-start-auto [@container(min-width:900px)]:mt-0';
const COL_SPAN = 'row-span-2 [@container(min-width:900px)]:row-span-1';
const COL_TARGET = `${COL_SPAN} text-right`;
const COL_LADDER = `${COL_SPAN} pl-3 [@container(min-width:900px)]:pl-4`;
// Pinned to the right edge so the row actions stay in view if the table ever has
// to scroll sideways; it extends through the row's right padding to cover it.
const COL_ACTIONS = `${COL_SPAN} sticky right-0 -mr-5 flex items-center justify-end gap-1 bg-inherit pr-5`;

/**
 * One policy, as a row.
 *
 * The columns are chosen to answer "will this do anything?" — reach, the two
 * targets, and whether the escalation ladder can fire — because everything on
 * this screen can be configured and still be inert, and the old card showed
 * none of it.
 */
function PolicyRow({
  policy,
  ladder,
  onEdit,
  onDelete,
  onToggleActive,
  onSetDefault,
  isDeleting,
}: PolicyRowProps) {
  const reach = reachOf(policy);
  const dead = !policy.isActive;

  return (
    <div
      className={`${ROW_GRID} border-b border-[var(--co-line-subtle)] px-5 py-[13px] last:border-b-0`}
      style={{
        background: dead ? 'var(--co-selected)' : 'var(--co-raised)',
        borderLeft: `2px solid ${
          reach === 'default-off'
            ? 'var(--co-burgundy)'
            : reach === 'default'
              ? 'var(--co-brass)'
              : 'transparent'
        }`,
      }}
    >
      <div className="min-w-0 pr-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className="min-w-0 max-w-full truncate text-[13.5px] font-semibold"
            style={{ color: dead ? 'var(--co-ink-3)' : 'var(--co-ink)' }}
          >
            {policy.name}
          </span>
          {policy.isDefault && (
            <span className="co-mono flex-none whitespace-nowrap rounded-[4px] border border-[var(--co-line-strong)] px-[6px] py-[2px] text-[9px] font-semibold uppercase tracking-[.1em] text-[var(--co-ink-3)]">
              Default
            </span>
          )}
          {policy.useBusinessHours && (
            <span
              title="The clock only runs during this tenant's working hours, so the elapsed time an agent sees is not wall-clock time."
              className="co-mono flex-none whitespace-nowrap rounded-[4px] border border-[var(--co-line-strong)] px-[6px] py-[2px] text-[9px] font-semibold uppercase tracking-[.1em] text-[var(--co-ink-3)]"
            >
              Business hours
            </span>
          )}
        </div>
        <div className="mt-[5px] truncate text-[11.5px] text-[var(--co-ink-3)]">
          {policy.description || 'No description'}
        </div>
      </div>

      <div className={`${COL_REACH} text-[11.5px] leading-[1.45] text-[var(--co-ink-2)]`}>
        {REACH_TEXT[reach]}
      </div>

      <div className={`co-mono ${COL_TARGET} text-[12px] font-semibold tabular-nums`}>
        <span
          style={{
            color: policy.firstResponseTarget ? 'var(--co-ink)' : 'var(--co-ink-4)',
          }}
        >
          {targetText(policy.firstResponseTarget)}
        </span>
      </div>

      <div className={`co-mono ${COL_TARGET} text-[12px] font-semibold tabular-nums`}>
        <span
          style={{ color: policy.resolutionTarget ? 'var(--co-ink)' : 'var(--co-ink-4)' }}
        >
          {targetText(policy.resolutionTarget)}
        </span>
      </div>

      <div
        className={`co-mono ${COL_LADDER} text-[10.5px] tracking-[.04em]`}
        style={LADDER_TONE[ladder.state]}
      >
        {ladder.text}
      </div>

      <div className={COL_ACTIONS}>
        {!policy.isDefault && policy.isActive && (
          <button
            type="button"
            onClick={onSetDefault}
            title="Make this the policy every unrouted ticket gets"
            className="rounded-md p-[6px] text-[var(--co-ink-4)] hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
          >
            <Star className="h-[15px] w-[15px]" />
          </button>
        )}
        <button
          type="button"
          onClick={onToggleActive}
          title={policy.isActive ? 'Switch off — it stops applying to new tickets' : 'Switch on'}
          className="rounded-md p-[6px] text-[var(--co-ink-4)] hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
        >
          {policy.isActive ? (
            <ToggleRight className="h-[15px] w-[15px] text-[var(--co-green)]" />
          ) : (
            <ToggleLeft className="h-[15px] w-[15px]" />
          )}
        </button>
        <button
          type="button"
          onClick={onEdit}
          title="Edit"
          className="rounded-md p-[6px] text-[var(--co-ink-4)] hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
        >
          <Edit2 className="h-[15px] w-[15px]" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          title="Delete"
          className="rounded-md p-[6px] text-[var(--co-ink-4)] hover:bg-[var(--co-burgundy-wash)] hover:text-[var(--co-burgundy)] disabled:opacity-40"
        >
          <Trash2 className="h-[15px] w-[15px]" />
        </button>
      </div>
    </div>
  );
}

export default function SlaSettings() {
  const {
    policies,
    isLoading,
    isSaving,
    isDeleting,
    error,
    fetchPolicies,
    createPolicy,
    updatePolicy,
    deletePolicy,
    toggleActive,
    setAsDefault,
  } = useSlaStore();
  const { schedules: businessHoursSchedules, fetchSchedules } = useBusinessHoursStore();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<SlaPolicy | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<SlaPolicy | null>(null);

  // What each stored escalation ladder will ACTUALLY do. Without it, a policy
  // showing "3 levels" is indistinguishable from one that escalates to nobody —
  // the failure this report was written to surface.
  //
  // Fetched with an effect rather than a query hook to match the rest of this
  // screen, which runs on the SLA store; adding a react-query dependency here
  // would make the component unmountable without a provider for the sake of one
  // read.
  //
  // Admin-only endpoint: a supervisor opening this page gets a 403 and the
  // ladder column then reads "unchecked" rather than claiming a health it has
  // not verified. Not retried — one refusal is the answer.
  const [configReport, setConfigReport] = useState<EscalationConfigReport | undefined>();

  useEffect(() => {
    let cancelled = false;
    escalationHistoryService
      .configReport()
      .then((report) => {
        if (!cancelled) setConfigReport(report);
      })
      .catch(() => {
        /* not permitted, or unavailable — the ladder column says "unchecked" */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ladderHealth = useMemo(
    () => new Map((configReport?.policies ?? []).map((p) => [p.policyId, p])),
    [configReport],
  );
  const rows = useMemo(() => readingOrder(policies), [policies]);
  const summary = useMemo(() => summarise(policies), [policies]);
  const problems = useMemo(
    () => findings(policies, configReport),
    [policies, configReport],
  );

  useEffect(() => {
    fetchPolicies();
    fetchSchedules();
  }, [fetchPolicies, fetchSchedules]);

  /**
   * Reason the last store action failed. Read through `getState()` rather than
   * the destructured `error` above, which is the render-time value and is always
   * stale immediately after an await.
   */
  const failureReason = (fallback = 'Please try again.') =>
    useSlaStore.getState?.().error || fallback;

  const handleCreate = async (data: CreateSlaPolicyDto) => {
    const result = await createPolicy(data);
    if (result) {
      toast.success('Policy created', `"${result.name}" is ready to use.`);
      setShowForm(false);
    } else {
      toast.error('Could not create policy', failureReason());
    }
  };

  const handleUpdate = async (data: CreateSlaPolicyDto) => {
    if (!editingPolicy) return;
    const result = await updatePolicy(editingPolicy.id, data);
    if (result) {
      toast.success('Policy updated', `Changes to "${result.name}" were saved.`);
      setEditingPolicy(null);
    } else {
      toast.error('Could not update policy', failureReason());
    }
  };

  const handleDelete = async () => {
    if (!policyToDelete) return;
    setDeletingId(policyToDelete.id);
    // Captured before the delete — `policyToDelete` is cleared on success.
    const deletedName = policyToDelete.name;
    const success = await deletePolicy(policyToDelete.id);
    if (success) {
      toast.success('Policy deleted', `"${deletedName}" was removed.`);
      setShowDeleteConfirm(false);
      setPolicyToDelete(null);
    } else {
      toast.error('Could not delete policy', failureReason());
    }
    setDeletingId(null);
  };

  // `policy.isActive` is the state BEFORE the toggle, so an active policy here
  // means we just deactivated it.
  const handleToggleActive = async (policy: SlaPolicy) => {
    const wasActive = policy.isActive;
    const ok = await toggleActive(policy.id);
    if (ok) {
      toast.success(
        wasActive ? 'Policy disabled' : 'Policy enabled',
        wasActive
          ? `"${policy.name}" no longer applies to new tickets.`
          : `"${policy.name}" now applies to new tickets.`,
      );
    } else {
      toast.error(
        wasActive ? 'Could not disable policy' : 'Could not enable policy',
        failureReason(),
      );
    }
  };

  const handleSetDefault = async (policy: SlaPolicy) => {
    const ok = await setAsDefault(policy.id);
    if (ok) {
      toast.success('Default policy updated', `"${policy.name}" is now the default.`);
    } else {
      toast.error('Could not set default policy', failureReason());
    }
  };

  const openDeleteConfirm = (policy: SlaPolicy) => {
    setPolicyToDelete(policy);
    setShowDeleteConfirm(true);
  };

  if (isLoading && policies.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* The subtitle states the one fact that governs the whole screen: which
          policy a ticket gets when nothing chose one for it. */}
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h2 className="text-[22px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
            SLA policies
          </h2>
          <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
            {summary.total === 0
              ? 'No policies yet'
              : summary.defaultName
                ? `${summary.active} active · unrouted tickets get "${summary.defaultName}"`
                : `${summary.active} active · no default, so unrouted tickets get no SLA`}
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowForm(true)}
          disabled={showForm || editingPolicy !== null}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Policy
        </Button>
      </div>

      {/* Everything configured on this screen that will silently do nothing.
          A full-width band above the table, not a cell inside it — a warning
          that scrolls away inside a list is a warning nobody reads. */}
      {problems.map((finding) => (
        <div
          key={finding.id}
          className="flex items-start gap-3 rounded-[10px] border px-[18px] py-[14px]"
          style={
            finding.tone === 'burgundy'
              ? {
                  borderColor: 'var(--co-burgundy-line)',
                  background: 'var(--co-burgundy-wash)',
                }
              : {
                  borderColor: 'var(--co-terracotta-line)',
                  background: 'var(--co-terracotta-wash)',
                }
          }
        >
          <div
            className="mt-[1px] flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md text-[13px] font-bold text-[var(--co-ink-invert)]"
            style={{
              background:
                finding.tone === 'burgundy' ? 'var(--co-burgundy)' : 'var(--co-terracotta)',
            }}
          >
            !
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="co-mono text-[10px] font-semibold uppercase tracking-[.14em]"
              style={{
                color:
                  finding.tone === 'burgundy' ? 'var(--co-burgundy)' : 'var(--co-terracotta)',
              }}
            >
              {finding.headline}
            </div>
            <p
              className="mt-[5px] max-w-[92ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-2)]"
              style={{ textWrap: 'pretty' } as React.CSSProperties}
            >
              {finding.body}
            </p>
          </div>
          {finding.policyId && (
            <button
              type="button"
              onClick={() => {
                const target = policies.find((p) => p.id === finding.policyId);
                if (target) setEditingPolicy(target);
              }}
              className="co-mono flex-none whitespace-nowrap rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-[7px] text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--co-ink-2)] hover:border-[var(--co-ink)]"
            >
              Open policy
            </button>
          )}
        </div>
      ))}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 dark:bg-red-900/20 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Create Form */}
      {showForm && (
        <div className="p-6 border border-primary-200 bg-primary-50 rounded-lg dark:border-primary-500/40 dark:bg-gray-800/60">
          <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">Create SLA Policy</h3>
          <SlaPolicyForm
            schedules={businessHoursSchedules}
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isLoading={isSaving}
          />
        </div>
      )}

      {/* Edit dialog — editing opens in a popup rather than an inline panel, so
          the policy is never shown twice (form + its own read-only card). */}
      <Modal
        isOpen={editingPolicy !== null}
        onClose={() => setEditingPolicy(null)}
        title="Edit SLA Policy"
        size="xl"
      >
        {editingPolicy && (
          <SlaPolicyForm
            policy={editingPolicy}
            schedules={businessHoursSchedules}
            onSubmit={handleUpdate}
            onCancel={() => setEditingPolicy(null)}
            isLoading={isSaving}
          />
        )}
      </Modal>

      {/* The policies themselves. Ordered for reading, not for matching —
          there is no match order in this system, and drawing one would be
          inventing an engine that does not exist. */}
      {policies.length === 0 && !showForm ? (
        <div className="rounded-[10px] border border-dashed border-[var(--co-line-strong)] bg-[var(--co-raised)] px-6 py-12 text-center">
          <Clock className="mx-auto mb-4 h-10 w-10 text-[var(--co-ink-4)]" />
          <h3 className="text-[15px] font-semibold text-[var(--co-ink)]">No SLA policies</h3>
          <p className="mx-auto mt-2 max-w-[54ch] text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">
            Until one exists, no ticket carries a response or resolution target — nothing can
            breach, and the SLA report has nothing to measure.
          </p>
          <div className="mt-5 flex justify-center">
            <Button variant="primary" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Policy
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] [container-type:inline-size]"
          style={{ boxShadow: 'var(--co-shadow-card)' }}
        >
          {/* Fits without scrolling at every supported width. Only a card narrower
              than the floor below scrolls sideways, rather than crushing the name,
              and the actions column stays pinned. */}
          <div className="scrollbar-thin overflow-x-auto">
          <div className="min-w-[620px]">
          <div className={`co-mono ${ROW_GRID} border-b border-[var(--co-line)] px-5 pb-[11px] pt-[14px] text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]`}>
            <div className={`${COL_SPAN} min-w-0 pr-3`}>Policy</div>
            <div className={`${COL_REACH} hidden [@container(min-width:900px)]:block`}>Applies to</div>
            <div className={COL_TARGET}>First response</div>
            <div className={COL_TARGET}>Resolution</div>
            <div className={COL_LADDER}>Escalation</div>
            <div className={`${COL_ACTIONS} bg-[var(--co-raised)]`} />
          </div>

          {rows.map((policy) => (
            <PolicyRow
              key={policy.id}
              policy={policy}
              ladder={ladderOf(policy, ladderHealth.get(policy.id))}
              onEdit={() => setEditingPolicy(policy)}
              onDelete={() => openDeleteConfirm(policy)}
              onToggleActive={() => handleToggleActive(policy)}
              onSetDefault={() => handleSetDefault(policy)}
              isDeleting={deletingId === policy.id}
            />
          ))}
          </div>
          </div>

          {/* The artboard's "applied in order, first match wins" replaced by how
              a policy actually reaches a ticket here. A stated rule that is not
              the real one is worse than no rule stated. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-3">
            <span className="co-mono whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
              {`${summary.total} ${summary.total === 1 ? 'policy' : 'policies'}${
                summary.inactive ? ` · ${summary.inactive} inactive` : ''
              }`}
            </span>
            <div className="flex-1" />
            <span className="text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
              A ticket gets the policy its pipeline names, or the default. There is no match order.
            </span>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPolicyToDelete(null);
        }}
        title="Delete SLA Policy"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 rounded-full dark:bg-red-900/30">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Delete &quot;{policyToDelete?.name}&quot;?
              </h3>
              <p className="text-gray-500 mt-2 dark:text-gray-400">
                This action cannot be undone. Tickets using this SLA policy will need to be
                reassigned to another policy.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setPolicyToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Policy
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
